import { useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import VersionLabel from '@/pages/product/components/VersionWrapper/components/VersionLabel';
import { getVersionDesc, getVersionItemIntro, getVersionName } from '@/pages/product/config/ProductVersion.config';
import { ProductTracker } from '@/utils/Tracker';
import LinearItemWrap from '@/components/LinearItemWrap';
import { isEmpty, isSameProductName } from '@/utils';
import LazyLoadImg from '@/components/LazyLoadImg';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import { formatPriceThousands } from '@/utils/configs/price.config';
import styles from './VersionItem.module.less';
import cls from 'classnames';
import classNames from 'classnames';

/**
 * 规格单个选项item
 * @param {function} onChangeCommodity 点选后回调
 * @param {array} labels item右上角的标签
 * @param {object} commodity 套餐信息 / 规格则传入第一个套餐的信息
 * @param {object} version 规格信息
 * @param {boolean} isLastAttrList 最后一个属性列表
 * @param {boolean} isSpecStyle 当前样式是规格的样式
 * @param {string} className
 */
function Index(props) {
  const {
    labels,
    onChangeCommodity,
    commodity = {},
    commodity: { id: cid },
    version,
    isLastAttrList,
    className,
    isSpecStyle,
    product: {
      curSelectCommodityId,
      productDetail: { id: pid, VersionConfig },
      curSelectCommodity,
    },
    area: {
      LANG,
      country: { X_Language, currency_signal, currency },
      country,
    },
  } = props;

  // 当前item是否选中,根据curSelectCommodity自动判断
  const active = useMemo(() => version.cids.includes(curSelectCommodityId), [curSelectCommodityId, version]);

  // 当前item是否无库存, 规格逻辑相同：如果连第一个套餐都无库存了，此规格肯定也🈚️了
  const isOutOfStock = useMemo(() => commodity.isOutOfStock, [commodity.isOutOfStock]);

  const isVirtualTour = pid === 21 && ![2558, 2564, 2570, 2576, 2582, 2588].includes(cid); // 是否为房产套餐且有权益的套餐，是的话展示价格为0

  // 当前属性并没有选中项其他属性的套餐，为true时会将属性选项变灰
  const isNoItem = useMemo(() => {
    // 属性数组只有一个列表无需进行过滤，除了go3单相机
    if (VersionConfig.attrList?.length <= 1 && pid !== 475) {
      return false;
    }
    // 哆啦A梦特殊处理, 颜色属性不会置灰
    if (version.cids.length === 1 && version.cids.includes(3683)) {
      return false;
    }
    const versionName = curSelectCommodity?.versionName;
    const commodity = version.list.find((d) => {
      const dVersionNames = d?.versionName;
      return versionName.some((name, index) => {
        if (index === 0) {
          return pid !== 475 ? false : isSameProductName(dVersionNames[index], name, 'equal');
        }
        return isSameProductName(dVersionNames[index], name, 'equal');
      });
    });
    return !commodity;
  }, [VersionConfig.attrList?.length, curSelectCommodity?.versionName, version.list]);

  // X4房产套餐特殊处理
  const isX4VirtualTourSpecial = useMemo(() => {
    const versionName = getVersionName({
      cids: version.cids,
      X_Language,
      commodityName: version.versionName,
      LANG,
    });
    return pid === 21 && curSelectCommodity.commodityName?.includes('X4') && versionName === 'CloudPano';
  }, [pid, curSelectCommodity.commodityName, version]);

  /** 选中套餐 */
  const handleClick = () => {
    // 当前属性下没有可选套餐
    // if (isNoItem) return;
    // 特殊套餐不允许点击
    if (isX4VirtualTourSpecial) return;
    onChangeCommodity(cid);
    ProductTracker.productPage({
      action: 'spec_select',
      productState: props.product,
    });
  };

  /** 获取套餐名前的icon */
  const Icon = useMemo(() => {
    try {
      if (version.groupStyle?.icon) {
        const { icon } = version.groupStyle || {};
        return icon.indexOf('http') < 2 && icon.indexOf('http') >= 0 ? (
          <LazyLoadImg webp={!icon.includes('svg')} className={styles.colorIcon} src={icon} alt='' />
        ) : (
          <span className={styles.colorIcon} dangerouslySetInnerHTML={{ __html: icon }} />
        );
      }
    } catch (e) {
      armsErrorSend(e, { filename: 'get commodityName Icon', data: { groupStyle: version.groupStyle } });
    }
  }, [version.groupStyle]);

  /** 获取套餐详情的描述Intro */
  const itemIntro = useMemo(() => {
    return getVersionItemIntro({ pid, cid, LANG });
  }, [LANG, cid, pid]);

  const showItemIntro = useMemo(() => {
    if (isEmpty(itemIntro)) return false;
    return true;
  }, [itemIntro]);

  /** 获取套餐简介 */
  const itemDesc = useMemo(() => {
    return getVersionDesc({ pid, cid, LANG });
  }, [LANG, cid, pid]);

  const showItemDesc = useMemo(() => {
    if (isEmpty(itemDesc)) return false;
    return true;
  }, [itemDesc]);

  // 以下这些情况展示缺货样式（透明度0.5)
  const showOutOfStockStyle = isOutOfStock || isNoItem || isX4VirtualTourSpecial;

  // 最后一个属性 && 没有可选套餐就隐藏（为了兼容go3黑色隐藏32GB版本以及go3黑色版本单相机）
  if (!cid || (isNoItem && isLastAttrList && pid !== 475)) return null;

  return (
    <div
      className={cls(styles.itemContainer, isSpecStyle ? styles.spec : styles.attr, className)}
      data-pid={pid}
      data-icon={!!version?.groupStyle?.icon}
    >
      <div className={styles.labelWrap}>
        {labels?.map(
          (label, k) =>
            label && (
              <div className={styles.label} key={k}>
                <VersionLabel label={label} />
              </div>
            )
        )}
      </div>
      <ComponentsErrorBoundary key={cid} name='VersionItem LinearItemWrap' data={{ cid, active, version }}>
        <LinearItemWrap
          key={cid}
          active={active}
          theme={isOutOfStock ? 'outOfStock' : 'productBlack'}
          onClick={handleClick}
          className={classNames(styles.versionItem, showOutOfStockStyle ? styles.disable : '')}
        >
          {Icon}
          <div className={styles.itemWrap}>
            <div className={styles.nameWrap} data-lang={X_Language}>
              <div
                dangerouslySetInnerHTML={{
                  __html: getVersionName({
                    cids: version.cids,
                    X_Language,
                    commodityName: version.versionName,
                    LANG,
                  }),
                }}
              />
              {showItemDesc && <div className={styles.versionDesc}>{itemDesc}</div>}
            </div>
            {isVirtualTour && (
              <div className={styles.priceWrap}>
                {formatPriceThousands({
                  price: 0,
                  signal: currency_signal,
                  currency: currency,
                  country,
                })}
              </div>
            )}
          </div>
        </LinearItemWrap>
      </ComponentsErrorBoundary>

      {active && showItemIntro && (
        <div className={styles.itemIntro}>
          <div className={styles.introWrap} dangerouslySetInnerHTML={{ __html: itemIntro }} />
        </div>
      )}
    </div>
  );
}
const VersionItem = inject('product', 'area', 'app')(observer(Index));
export default VersionItem;
